import {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useContext,
    useMemo,
    useState,
} from 'react';
import { useLocalStorage } from 'utils/hooks/useStorage';
import { UNHEALTHY_STATUSES } from 'utils/siteStatus';
import {
    DEFAULT_FILTER_BY,
    DEFAULT_GROUP_BY,
    DEFAULT_GROUP_EXPANDED_STATE,
    DEFAULT_SORT_BY,
} from './constants';
import { FilterBy, GroupBy, GroupExpandedState, SortBy } from './types';

interface SiteSelectorContextValue {
    filterBy: FilterBy;
    setFilterBy: (filterBy: FilterBy) => void;
    groupBy: GroupBy;
    setGroupBy: (groupBy: GroupBy) => void;
    sortBy: SortBy;
    setSortBy: (sortBy: SortBy) => void;
    groupExpandedState: GroupExpandedState | undefined;
    setGroupExpandedState: Dispatch<SetStateAction<GroupExpandedState>>;
    groupExpandedStateExclude: (string | number)[];
    setGroupExpandedStateExclude: Dispatch<SetStateAction<(string | number)[]>>;
    urlSearchParams: string[][];
    visible: boolean;
}

export const SiteSelectorContext = createContext<SiteSelectorContextValue>({
    filterBy: DEFAULT_FILTER_BY,
    setFilterBy: () => {},
    groupBy: DEFAULT_GROUP_BY,
    setGroupBy: () => {},
    sortBy: DEFAULT_SORT_BY[DEFAULT_GROUP_BY],
    setSortBy: () => {},
    groupExpandedState: DEFAULT_GROUP_EXPANDED_STATE,
    setGroupExpandedState: () => {},
    groupExpandedStateExclude: [],
    setGroupExpandedStateExclude: () => {},
    urlSearchParams: [],
    visible: true,
});

interface SiteSelectorProviderProps extends PropsWithChildren {
    visible?: boolean;
}

export const SiteSelectorContextProvider = ({
    children,
    visible,
}: SiteSelectorProviderProps) => {
    const [filterBy, setFilterBy] = useLocalStorage<FilterBy>(
        'siteSelectorFilterBy',
        DEFAULT_FILTER_BY
    );

    const [groupBy, setGroupBy] = useLocalStorage<GroupBy>(
        'siteSelectorGroupBy',
        DEFAULT_GROUP_BY
    );

    const [sortBy, setSortBy] = useLocalStorage<SortBy>(
        'siteSelectorSortBy',
        DEFAULT_SORT_BY[groupBy]
    );

    const [groupExpandedState, setGroupExpandedState] = useLocalStorage(
        'siteSelectorGroupExpandedState',
        DEFAULT_GROUP_EXPANDED_STATE
    );

    const [groupExpandedStateExclude, setGroupExpandedStateExclude] = useState<
        (string | number)[]
    >([]);

    const urlSearchParams = useMemo(
        () => [
            ['sort_by', sortBy],
            ...Object.entries(filterBy).flatMap(
                ([filterProperty, filterValue]) =>
                    Array.isArray(filterValue)
                        ? filterValue.flatMap((value) => {
                              // special case to split up unhealthy status to API equivalent
                              if (value === 'unhealthy') {
                                  return UNHEALTHY_STATUSES.map((status) => [
                                      filterProperty,
                                      status,
                                  ]);
                              }

                              return [[filterProperty, value.trim()]];
                          })
                        : [[filterProperty, filterValue.trim()]]
            ),
        ],
        [filterBy, sortBy]
    );

    return (
        <SiteSelectorContext
            value={useMemo(
                () => ({
                    filterBy,
                    setFilterBy,
                    groupBy,
                    setGroupBy: (groupBy) => {
                        setGroupBy(groupBy);
                        /*
                         * Reset `sortBy` when `groupBy` changes because sorting
                         * options are different for each grouping and not
                         * necessarily compatible with each other. Logically, it
                         * would be better to make this an effect rather than
                         * include it in handler but in this case it is crucial
                         * for the two updates to happen at the exact same time.
                         */
                        setSortBy(DEFAULT_SORT_BY[groupBy]);
                        /*
                         * Reset `groupExpandedStateExclude` when `groupBy`
                         * changes to go back to the initial **all** groups
                         * expanded/collapsed state.
                         */
                        setGroupExpandedStateExclude([]);
                    },
                    sortBy,
                    setSortBy,
                    groupExpandedState,
                    setGroupExpandedState: (nextGroupExpandedState) => {
                        setGroupExpandedState(nextGroupExpandedState);
                        setGroupExpandedStateExclude([]);
                    },
                    groupExpandedStateExclude,
                    setGroupExpandedStateExclude,
                    urlSearchParams,
                    visible: !!visible,
                }),
                [
                    filterBy,
                    setFilterBy,
                    groupBy,
                    setGroupBy,
                    sortBy,
                    setSortBy,
                    groupExpandedState,
                    setGroupExpandedState,
                    groupExpandedStateExclude,
                    setGroupExpandedStateExclude,
                    urlSearchParams,
                    visible,
                ]
            )}
        >
            {children}
        </SiteSelectorContext>
    );
};

export const useSiteSelectorContext = () => useContext(SiteSelectorContext);
